import { createContext, useContext, useState, useCallback, useMemo } from "react";
import { WHATSAPP_LINK } from "../data/menu";

const CartContext = createContext(null);

// Build a unique key per product+size combo (so Pepperoni Personal and Pepperoni Grande are separate)
const buildKey = (id, size) => (size ? `${id}::${size}` : id);

export const CartProvider = ({ children }) => {
  const [items, setItems] = useState([]); // { key, id, name, price, qty, size? }
  const [drawerOpen, setDrawerOpen] = useState(false);

  // product: { id, name, price, ...} ; optional variant: { size, price } for pizzas
  const addItem = useCallback((product, variant = null) => {
    const size = variant?.size || null;
    const price = variant?.price ?? product.price;
    const key = buildKey(product.id, size);
    const displayName = size ? `${product.name} · ${size}` : product.name;

    setItems((prev) => {
      const existing = prev.find((p) => p.key === key);
      if (existing) {
        return prev.map((p) =>
          p.key === key ? { ...p, qty: p.qty + 1 } : p
        );
      }
      return [
        ...prev,
        {
          key,
          id: product.id,
          name: displayName,
          price,
          size,
          qty: 1,
        },
      ];
    });
  }, []);

  const removeItem = useCallback((key) => {
    setItems((prev) => prev.filter((p) => p.key !== key));
  }, []);

  const decrementItem = useCallback((key) => {
    setItems((prev) =>
      prev
        .map((p) => (p.key === key ? { ...p, qty: p.qty - 1 } : p))
        .filter((p) => p.qty > 0)
    );
  }, []);

  const incrementItem = useCallback((key) => {
    setItems((prev) =>
      prev.map((p) => (p.key === key ? { ...p, qty: p.qty + 1 } : p))
    );
  }, []);

  const clearCart = useCallback(() => setItems([]), []);

  const totalItems = useMemo(
    () => items.reduce((sum, p) => sum + p.qty, 0),
    [items]
  );

  const totalPrice = useMemo(
    () => items.reduce((sum, p) => sum + p.qty * p.price, 0),
    [items]
  );

  const buildWhatsAppUrl = useCallback(() => {
    if (items.length === 0) return WHATSAPP_LINK;

    const formatter = new Intl.NumberFormat("es-MX", {
      style: "currency",
      currency: "MXN",
      maximumFractionDigits: 0,
    });

    const lines = items.map(
      (p) => `• ${p.qty}x ${p.name} — ${formatter.format(p.price * p.qty)}`
    );

    const message =
      "¡Hola Pizzería Estilo Gali! 🍕\n\n" +
      "Me gustaría pedir:\n" +
      lines.join("\n") +
      `\n\nTotal: ${formatter.format(totalPrice)}\n\n` +
      "¿Me confirman disponibilidad y tiempo de entrega, por favor?";

    return `https://wa.me/527421177698?text=${encodeURIComponent(message)}`;
  }, [items, totalPrice]);

  const value = {
    items,
    addItem,
    removeItem,
    decrementItem,
    incrementItem,
    clearCart,
    totalItems,
    totalPrice,
    drawerOpen,
    setDrawerOpen,
    openDrawer: () => setDrawerOpen(true),
    closeDrawer: () => setDrawerOpen(false),
    buildWhatsAppUrl,
  };

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};

export const useCart = () => {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error("useCart must be used inside CartProvider");
  return ctx;
};
